import { StockPolicyMode } from "../types/enums/StockPolicyMode";
import { StockWarningType } from "../types/enums/StockWarningType";
import {
  DBStockPolicy,
  StockPolicyAssignment,
  StockPolicyPeriod,
} from "../types/stockPolicy";

export type StockLevels = {
  min: number | null;
  max: number | null;
  warnAboveMax: boolean;
};

export type StockEvaluation = StockLevels & {
  type: StockWarningType;
  suggestedOrder: number | null;
  excess: number | null;
};

const roundQuantity = (value: number) => Math.round(value * 1000) / 1000;

const monthDay = (isoDate: string) => isoDate.slice(5, 10);

export const isPeriodActive = (
  period: Pick<StockPolicyPeriod, "start" | "end" | "repeatsYearly">,
  businessDate: string,
) => {
  if (!period.repeatsYearly) {
    return period.start <= businessDate && businessDate <= period.end;
  }
  const start = monthDay(period.start);
  const end = monthDay(period.end);
  const today = monthDay(businessDate);
  return start <= end
    ? start <= today && today <= end
    : today >= start || today <= end;
};

export const resolveStockLevels = (
  assignment: StockPolicyAssignment,
  policy: Pick<
    DBStockPolicy,
    "mode" | "min" | "max" | "warnAboveMax" | "periods"
  > | null,
  businessDate: string,
): StockLevels | null => {
  const override = (base: StockLevels): StockLevels => ({
    min: assignment.min ?? base.min,
    max: assignment.max ?? base.max,
    warnAboveMax: base.warnAboveMax,
  });

  if (assignment.policyId === null) {
    return override({ min: null, max: null, warnAboveMax: false });
  }
  if (!policy) {
    return null;
  }

  const period = policy.periods.find((candidate) =>
    isPeriodActive(candidate, businessDate),
  );
  const base: StockLevels = {
    min: period?.min ?? policy.min,
    max: period?.max ?? policy.max,
    warnAboveMax: policy.warnAboveMax,
  };

  switch (policy.mode) {
    case StockPolicyMode.NOS:
      return override(base);
    case StockPolicyMode.SEASONAL:
      return period ? override(base) : null;
    default:
      return null;
  }
};

const classify = (quantity: number, levels: StockLevels) => {
  if (quantity < 0) {
    return StockWarningType.NEGATIVE;
  }
  if (quantity === 0) {
    return StockWarningType.OUT_OF_STOCK;
  }
  if (levels.min !== null && quantity < levels.min) {
    return StockWarningType.BELOW_MIN;
  }
  if (levels.warnAboveMax && levels.max !== null && quantity > levels.max) {
    return StockWarningType.ABOVE_MAX;
  }
  return null;
};

export const evaluateStock = (
  quantity: number | null | undefined,
  assignment: StockPolicyAssignment | null | undefined,
  policy: Parameters<typeof resolveStockLevels>[1],
  businessDate: string,
): StockEvaluation | null => {
  if (quantity === null || quantity === undefined || !assignment) {
    return null;
  }
  const levels = resolveStockLevels(assignment, policy, businessDate);
  if (!levels) {
    return null;
  }
  const type = classify(quantity, levels);
  if (!type) {
    return null;
  }
  if (type === StockWarningType.ABOVE_MAX) {
    return {
      ...levels,
      type,
      suggestedOrder: null,
      excess: levels.max === null ? null : roundQuantity(quantity - levels.max),
    };
  }
  const target = levels.max ?? levels.min;
  return {
    ...levels,
    type,
    suggestedOrder:
      target === null ? null : Math.max(0, roundQuantity(target - quantity)),
    excess: null,
  };
};
